import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, firstValueFrom, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { RoleName, TokenResponse, User } from './models';

const TOKEN_KEY = 'mkt.token';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly url = `${environment.apiUrl}/auth`;

  private readonly tokenSignal = signal<string | null>(readToken());
  private readonly userSignal = signal<User | null>(null);
  private refreshing$: Observable<string> | null = null;

  readonly token = this.tokenSignal.asReadonly();
  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal() && !!this.userSignal());
  readonly isAdmin = computed(() => this.hasRole('admin'));

  /** Al arrancar la app: si hay token, recupera el usuario. */
  async restore(): Promise<void> {
    if (!this.tokenSignal()) return;
    try {
      const res = await firstValueFrom(this.http.get<{ data: User }>(`${this.url}/me`));
      this.userSignal.set(res.data);
    } catch {
      this.clear();
    }
  }

  login(email: string, password: string): Observable<User> {
    return this.http.post<TokenResponse>(`${this.url}/login`, { email, password }).pipe(map((res) => this.store(res)));
  }

  exchangeMicrosoftCode(code: string): Observable<User> {
    return this.http.post<TokenResponse>(`${this.url}/microsoft/exchange`, { code }).pipe(map((res) => this.store(res)));
  }

  microsoftLoginUrl(): string {
    return `${environment.authUrl}/auth/microsoft/redirect`;
  }

  /** Un único refresh en vuelo aunque fallen varias peticiones a la vez. */
  refresh(): Observable<string> {
    if (!this.refreshing$) {
      this.refreshing$ = this.http.post<TokenResponse>(`${this.url}/refresh`, {}).pipe(
        map((res) => {
          this.store(res);
          return res.access_token;
        }),
        catchError((err) => {
          this.clear();
          return throwError(() => err);
        }),
        finalize(() => (this.refreshing$ = null)),
        shareReplay(1),
      );
    }
    return this.refreshing$;
  }

  logout(): void {
    const request = this.tokenSignal() ? this.http.post(`${this.url}/logout`, {}).pipe(catchError(() => of(null))) : of(null);
    request.subscribe(() => {
      this.clear();
      this.router.navigate(['/login']);
    });
  }

  /** Cierra la sesión local sin llamar a la API (token ya inválido). */
  expire(): void {
    this.clear();
    this.router.navigate(['/login']);
  }

  hasRole(...roles: RoleName[]): boolean {
    const user = this.userSignal();
    return !!user && roles.some((r) => user.roles.includes(r));
  }

  can(permission: string): boolean {
    const user = this.userSignal();
    if (!user) return false;
    return user.roles.includes('admin') || user.permissions.includes(permission);
  }

  private store(res: TokenResponse): User {
    try {
      localStorage.setItem(TOKEN_KEY, res.access_token);
    } catch {
      /* sin almacenamiento: la sesión dura lo que la pestaña */
    }
    this.tokenSignal.set(res.access_token);
    this.userSignal.set(res.user);
    return res.user;
  }

  private clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignorar */
    }
    this.tokenSignal.set(null);
    this.userSignal.set(null);
  }
}
